import { X } from 'lucide-react';
import { useId, useRef, type CSSProperties, type ReactNode, type RefObject } from 'react';
import { createPortal } from 'react-dom';
import { cx } from '../../lib/cx.ts';
import { MEDIA, useBodyScrollLock, useFocusTrap, useMediaQuery } from '../../lib/hooks.ts';
import { IconButton } from './Button.tsx';
import { usePresence } from './usePresence.ts';
import './Drawer.css';

export interface DrawerProps {
  open: boolean;
  onClose: () => void;
  /** Visible title; when `hideHeader` is set it is used as the accessible name only. */
  title: string;
  description?: ReactNode;
  children: ReactNode;
  footer?: ReactNode;
  side?: 'left' | 'right';
  /** Panel width on tablet/desktop in px. */
  width?: number;
  /** Phones (<768px) get a bottom sheet instead of a side panel. */
  sheetOnPhone?: boolean;
  hideHeader?: boolean;
  initialFocus?: RefObject<HTMLElement | null>;
  id?: string;
  className?: string;
}

export function Drawer({
  open,
  onClose,
  title,
  description,
  children,
  footer,
  side = 'right',
  width = 420,
  sheetOnPhone = true,
  hideHeader = false,
  initialFocus,
  id,
  className,
}: DrawerProps) {
  const isPhone = useMediaQuery(MEDIA.phone);
  const { mounted, visible } = usePresence(open, 240);
  const panelRef = useRef<HTMLDivElement>(null);
  const titleId = useId();
  useFocusTrap(panelRef, open && mounted, { onEscape: onClose, initialFocus });
  useBodyScrollLock(mounted);

  if (!mounted) return null;
  const variant = sheetOnPhone && isPhone ? 'sheet' : side;

  return createPortal(
    <div className="drawer-root" data-state={visible ? 'open' : 'closed'}>
      <div className="drawer-backdrop" aria-hidden="true" onClick={onClose} />
      <div
        ref={panelRef}
        id={id}
        role="dialog"
        aria-modal="true"
        aria-label={hideHeader ? title : undefined}
        aria-labelledby={hideHeader ? undefined : titleId}
        tabIndex={-1}
        className={cx('drawer-panel', `drawer-panel--${variant}`, className)}
        style={{ '--drawer-w': `${width}px` } as CSSProperties}
      >
        {variant === 'sheet' && <div className="drawer-handle" aria-hidden="true" />}
        {!hideHeader && (
          <header className="drawer-header">
            <div className="drawer-heading">
              <h2 id={titleId} className="drawer-title">
                {title}
              </h2>
              {description && <p className="drawer-description">{description}</p>}
            </div>
            <IconButton icon={X} label="ปิด" size="sm" tooltip={false} onClick={onClose} />
          </header>
        )}
        <div className={cx('drawer-body', hideHeader && 'drawer-body--flush')}>{children}</div>
        {footer && <footer className="drawer-footer">{footer}</footer>}
      </div>
    </div>,
    document.body,
  );
}
